'use client';

import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { evaluateModelAccess } from '@flowmuse/billing/plans/access-eval';
import type { PlanLevels } from '@flowmuse/billing/plans/plan-access';
import { type DiscountSource } from '@flowmuse/billing/discount-multiplier';

export type ModelAccessEntry = {
  enabled: boolean;
  requiredPlan: string;
  requiredInterval: string;
  hidden: boolean;
  requiredTags?: string[] | null;
  /** 多语言权限描述 { en, zh, ... }，未命中时回退到 requiredTags 名 */
  accessLabels?: Record<string, string> | null;
  /** 多语言展示徽标 { en, zh, ... }（如 4K / HD），注入到模型上以 badgeI18n 渲染 */
  badges?: Record<string, string> | null;
  /** 多语言展示名 { en, zh, ... }，注入到模型上覆盖前端硬编码 labelI18n */
  labels?: Record<string, string> | null;
  /** 多语言备注 { en, zh, ... } */
  notes?: Record<string, string> | null;
  /** 多语言分类 { en, zh, ... } */
  categories?: Record<string, string> | null;
  sortOrder?: number | null;
  isNew?: boolean | null;
};
export type ModelAccessMap = Record<string, ModelAccessEntry>;

export type ModelPassMap = Record<string, number>;

export type SubscriptionContextType = {
  isSubscribed: boolean;
  discountMap?: Record<string, unknown> | null;
  /** 每个模型生效折扣的来源（global/user/plan/org），供 UI 标明折扣类型。 */
  discountSourceMap?: Record<string, DiscountSource> | null;
  /** 模型卡（次卡）折扣 map：只作用于次卡消耗数量，供 UI 展示徽标。 */
  passDiscountMap?: Record<string, number> | null;
  modelAccessMap?: ModelAccessMap | null;
  modelPassMap?: ModelPassMap | null;
  consumeModelPass: (modelId: string, count?: number) => void;
  userPlan?: string | null;
  userInterval?: string | null;
  userTags?: string[] | null;
  planLevels?: PlanLevels | null;
};

const noop = () => {};

const SubscriptionContext = createContext<SubscriptionContextType>({
  isSubscribed: false,
  discountMap: null,
  discountSourceMap: null,
  passDiscountMap: null,
  modelAccessMap: null,
  modelPassMap: null,
  consumeModelPass: noop,
  userPlan: null,
  userInterval: null,
  userTags: null,
  planLevels: null,
});

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

type AccessMeta = {
  _accessDenied?: boolean;
  _hasModelPass?: boolean;
  _requiredPlan?: string;
  _requiredInterval?: string;
  _requiredTags?: string[];
  _accessLabels?: Record<string, string>;
  labelI18n?: Record<string, string>;
  noteI18n?: Record<string, string>;
  categoryI18n?: Record<string, string>;
  badgeI18n?: Record<string, string>;
  order?: number;
  isNew?: boolean;
};

function applyModelAccessMeta<T>(model: T, rule: ModelAccessEntry): T & AccessMeta {
  const meta: Partial<AccessMeta> = {};
  if (rule.labels != null) meta.labelI18n = rule.labels;
  if (rule.notes != null) meta.noteI18n = rule.notes;
  if (rule.categories != null) meta.categoryI18n = rule.categories;
  if (rule.badges != null) meta.badgeI18n = rule.badges;
  if (typeof rule.sortOrder === 'number') meta.order = rule.sortOrder;
  if (typeof rule.isNew === 'boolean') meta.isNew = rule.isNew;
  return { ...model, ...meta } as T & AccessMeta;
}

/**
 * Client-side filter: removes hidden+disallowed models from a record,
 * and marks disallowed (but visible) models with `_accessDenied = true`.
 *
 * 判定逻辑由 {@link evaluateModelAccess} 统一提供（plan/interval gate 与
 * tag gate 之间为 OR）。本函数只负责把判定结果映射到 UI 元数据：
 *   - enabled=false（下架/维护中）→ 硬拒绝，次卡也不放行（hidden 时直接移除）
 *   - allowed → 直接挂上访问元数据
 *   - 否则若有 model pass 次卡 → 标 `_hasModelPass`
 *   - 否则若 rule.hidden=false → 标 `_accessDenied`，灰显并提示升级
 */
export function filterModelsClient<T>(
  models: Record<string, T>,
  accessMap: ModelAccessMap | null | undefined,
  userPlan: string | null | undefined,
  userInterval?: string | null | undefined,
  userTags?: string[] | null | undefined,
  passMap?: ModelPassMap | null | undefined,
  planLevels?: PlanLevels | null | undefined,
) {
  if (!accessMap) return models as Record<string, T & AccessMeta>;
  const result: Record<string, T & AccessMeta> = {};
  const subject = { plan: userPlan ?? null, interval: userInterval ?? null, tags: userTags ?? null };

  const deny = (model: T, rule: ModelAccessEntry): T & AccessMeta =>
    ({ ...applyModelAccessMeta(model, rule), _accessDenied: true, _requiredPlan: rule.requiredPlan, _requiredInterval: rule.requiredInterval, _requiredTags: rule.requiredTags ?? undefined, _accessLabels: rule.accessLabels ?? undefined });

  const passAllowed = (id: string) => passMap ? resolveModelPassRemaining(passMap, id) > 0 : false;

  for (const [id, model] of Object.entries(models)) {
    const rule = accessMap[id];
    if (!rule) { result[id] = model as T & AccessMeta; continue; }

    // enabled=false = 下架/维护中（模型供应异常），次卡也不放行，与服务端 precheckModelAccess 一致
    if (!rule.enabled) {
      if (!rule.hidden) { result[id] = deny(model, rule); }
      continue;
    }

    const { allowed } = evaluateModelAccess(rule, subject, planLevels ?? undefined);

    if (allowed) {
      result[id] = applyModelAccessMeta(model, rule);
    } else if (passAllowed(id)) {
      result[id] = { ...applyModelAccessMeta(model, rule), _hasModelPass: true } as T & AccessMeta;
    } else if (!rule.hidden) {
      result[id] = deny(model, rule);
    }
  }
  return result;
}

/**
 * Hook: returns models filtered by the current user's model access permissions.
 */
export function useFilteredModels<T>(models: Record<string, T>) {
  const { modelAccessMap, modelPassMap, userPlan, userInterval, userTags, planLevels } = useSubscription();
  return useMemo(
    () => filterModelsClient(models, modelAccessMap, userPlan, userInterval, userTags, modelPassMap, planLevels),
    [models, modelAccessMap, modelPassMap, userPlan, userInterval, userTags, planLevels],
  );
}

/**
 * 客户端查询某个模型的次卡剩余次数（精确 > 逗号分隔组 > 通配符）。
 */
export function resolveModelPassRemaining(
  passMap: ModelPassMap | null | undefined,
  modelId: string,
): number {
  if (!passMap) return 0;
  const exact = passMap[modelId];
  if (typeof exact === 'number' && exact > 0) return exact;
  for (const key in passMap) {
    if (key === '*' || !key.includes(',')) continue;
    const val = passMap[key];
    if (typeof val === 'number' && val > 0 && key.split(',').includes(modelId)) return val;
  }
  const wildcard = passMap['*'];
  if (typeof wildcard === 'number' && wildcard > 0) return wildcard;
  return 0;
}

function consumeFromPassMap(prev: ModelPassMap | null | undefined, modelId: string, count: number): ModelPassMap | null | undefined {
  if (!prev) return prev;
  if (typeof prev[modelId] === 'number' && prev[modelId] > 0) {
    const n = Math.min(prev[modelId], count);
    return { ...prev, [modelId]: prev[modelId] - n };
  }
  for (const key in prev) {
    if (key === '*' || !key.includes(',')) continue;
    const val = prev[key];
    if (typeof val === 'number' && val > 0 && key.split(',').includes(modelId)) {
      const n = Math.min(val, count);
      return { ...prev, [key]: val - n };
    }
  }
  if (typeof prev['*'] === 'number' && prev['*'] > 0) {
    const n = Math.min(prev['*'], count);
    return { ...prev, '*': prev['*'] - n };
  }
  return prev;
}

/**
 * Lightweight override for project pages: re-provides billing-dependent context
 * resolved from the concrete project owner rather than the active workspace.
 */
export const SubscriptionBillingOverride = ({
  children,
  userPlan,
  userInterval,
  isSubscribed,
  discountMap,
  discountSourceMap,
  passDiscountMap,
  modelPassMap,
  planLevels,
}: {
  children: ReactNode;
  userPlan?: string | null;
  userInterval?: string | null;
  isSubscribed: boolean;
  discountMap?: Record<string, unknown> | null;
  discountSourceMap?: Record<string, DiscountSource> | null;
  passDiscountMap?: Record<string, number> | null;
  modelPassMap?: ModelPassMap | null;
  planLevels?: PlanLevels | null;
}) => {
  const parent = useContext(SubscriptionContext);
  const [localPassMap, setLocalPassMap] = useState(modelPassMap);

  useEffect(() => {
    setLocalPassMap(modelPassMap);
  }, [modelPassMap]);

  const consumeModelPass = useCallback((modelId: string, count: number = 1) => {
    if (modelPassMap === undefined) {
      parent.consumeModelPass(modelId, count);
      return;
    }
    setLocalPassMap(prev => consumeFromPassMap(prev, modelId, count));
  }, [modelPassMap, parent]);

  const value = useMemo(
    () => ({
      ...parent,
      userPlan,
      userInterval,
      isSubscribed,
      discountMap: discountMap === undefined ? parent.discountMap : discountMap,
      discountSourceMap: discountSourceMap === undefined ? parent.discountSourceMap : discountSourceMap,
      passDiscountMap: passDiscountMap === undefined ? parent.passDiscountMap : passDiscountMap,
      modelPassMap: modelPassMap === undefined ? parent.modelPassMap : localPassMap,
      planLevels: planLevels === undefined ? parent.planLevels : planLevels,
      consumeModelPass,
    }),
    [parent, userPlan, userInterval, isSubscribed, discountMap, discountSourceMap, passDiscountMap, modelPassMap, localPassMap, planLevels, consumeModelPass],
  );
  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const SubscriptionProvider = ({
  children,
  isSubscribed,
  discountMap,
  discountSourceMap,
  passDiscountMap,
  modelAccessMap,
  modelPassMap: modelPassMapProp,
  userPlan,
  userInterval,
  userTags,
  planLevels,
}: {
  children: ReactNode;
  isSubscribed: boolean;
  discountMap?: Record<string, unknown> | null;
  discountSourceMap?: Record<string, DiscountSource> | null;
  passDiscountMap?: Record<string, number> | null;
  modelAccessMap?: ModelAccessMap | null;
  modelPassMap?: ModelPassMap | null;
  userPlan?: string | null;
  userInterval?: string | null;
  userTags?: string[] | null;
  planLevels?: PlanLevels | null;
}) => {
  const [passMap, setPassMap] = useState(modelPassMapProp);

  useEffect(() => {
    setPassMap(modelPassMapProp);
  }, [modelPassMapProp]);

  const consumeModelPass = useCallback((modelId: string, count: number = 1) => {
    setPassMap(prev => consumeFromPassMap(prev, modelId, count));
  }, []);

  const value = useMemo(
    () => ({ isSubscribed, discountMap, discountSourceMap, passDiscountMap, modelAccessMap, modelPassMap: passMap, consumeModelPass, userPlan, userInterval, userTags, planLevels }),
    [isSubscribed, discountMap, discountSourceMap, passDiscountMap, modelAccessMap, passMap, consumeModelPass, userPlan, userInterval, userTags, planLevels],
  );
  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
